/**
 * Per-key "last intent wins" writes. A group-cart quantity change sends an
 * absolute value; two taps sent concurrently could land out of order once a
 * version-conflict retry delayed the first (F31: + + made qty 3 on every
 * screen, then the late qty 2 overwrote it and the order fired 2). Writes for
 * one key run one at a time, and taps made meanwhile collapse to the newest.
 */
export function latestWins<T>() {
  const state = new Map<
    string,
    { running: Promise<void>; next?: { value: T; send: (v: T) => Promise<void> } }
  >();

  async function drain(key: string, value: T, send: (v: T) => Promise<void>) {
    let current: { value: T; send: (v: T) => Promise<void> } | undefined = { value, send };
    try {
      while (current) {
        try {
          await current.send(current.value);
        } catch (err) {
          // A newer tap is waiting: it supersedes the failed one (I4).
          if (!state.get(key)?.next) throw err;
        }
        const s = state.get(key);
        current = s?.next;
        if (s) s.next = undefined;
      }
    } finally {
      state.delete(key);
    }
  }

  return {
    /** Resolves once [value] (or a newer value for [key]) has been written. */
    run(key: string, value: T, send: (v: T) => Promise<void>): Promise<void> {
      const s = state.get(key);
      if (s) {
        s.next = { value, send };
        return s.running;
      }
      const entry: { running: Promise<void>; next?: { value: T; send: (v: T) => Promise<void> } } = {
        running: Promise.resolve(),
      };
      state.set(key, entry);
      entry.running = drain(key, value, send);
      return entry.running;
    },
  };
}
